import type { LscArchiveItem } from '../types';
import { nflTeamsMatch } from '../cfbBranding/teamVisualResolver';
import { displaySeasonLabel } from '../seasonDisplay';
import type { VodExpiryLabel } from './vodExpiryLabel';

export type BroadcastSide = 'home' | 'away';

export type BroadcasterMember = {
  twitch_login?: string | null;
  provider_login?: string | null;
  display_name?: string | null;
  current_side_label?: string | null;
};

export function replayKicker(archive: LscArchiveItem, edition?: string | null): string {
  const storedSeason = archive.match_summary?.season_label?.trim();
  const season = storedSeason
    ? displaySeasonLabel(storedSeason, null, archive.match_summary?.edition || edition)
    : '';
  const week = archive.match_summary?.stage_label?.trim();
  const parts: string[] = [];
  if (season) {
    parts.push(season);
  }
  if (week) {
    parts.push(week.toLowerCase().startsWith('week') ? week : `Week ${week}`);
  }
  parts.push('Replay');
  return parts.join(' · ');
}

export function expiryBannerCopy(label: VodExpiryLabel): string | null {
  if (label.kind !== 'may_expire') return null;
  return `${label.text}. Twitch removes past broadcasts after 7 to 60 days, depending on the streamer's account. Watch it soon.`;
}

function sameSideLabel(team: string, sideLabel: string): boolean {
  if (nflTeamsMatch(team, sideLabel)) return true;
  return team.trim().toLowerCase() === sideLabel.trim().toLowerCase();
}

/**
 * Side comes from the broadcaster's linked team measured against this match.
 * A stored home/away is used only when there is no linked team. Anything else
 * stays unknown so the picker does not invent a side.
 */
export function resolveBroadcastSide(input: {
  login?: string | null;
  storedSide?: string | null;
  awayLabel?: string | null;
  homeLabel?: string | null;
  members?: BroadcasterMember[];
}): BroadcastSide | null {
  const away = input.awayLabel?.trim() || '';
  const home = input.homeLabel?.trim() || '';
  const login = input.login?.trim().toLowerCase() || '';
  const member = (input.members ?? []).find((item) => {
    const twitch = item.twitch_login?.trim().toLowerCase();
    const provider = item.provider_login?.trim().toLowerCase();
    return Boolean(login) && (twitch === login || provider === login);
  });
  const team = member?.current_side_label?.trim();
  if (team) {
    const awayHit = Boolean(away) && sameSideLabel(team, away);
    const homeHit = Boolean(home) && sameSideLabel(team, home);
    if (awayHit && !homeHit) return 'away';
    if (homeHit && !awayHit) return 'home';
    return null;
  }
  const stored = (input.storedSide ?? '').trim().toLowerCase();
  if (stored === 'home' || stored === 'away') return stored;
  return null;
}

export function broadcastCaption(sources: Array<{ login?: string | null }>): string | null {
  const names = sources
    .map((source) => source.login?.trim())
    .filter((login): login is string => Boolean(login));
  return names.length > 0 ? names.join(' · ') : null;
}

export function withKnownDisplayName(
  source: LscArchiveItem,
  cards: LscArchiveItem[]
): LscArchiveItem {
  if ((source.provider_broadcaster_display_name ?? '').trim()) return source;
  for (const card of cards) {
    const match = [card, ...(card.sources ?? [])].find((item) => item.id === source.id);
    const name = match?.provider_broadcaster_display_name?.trim();
    if (name) return { ...source, provider_broadcaster_display_name: name };
  }
  return source;
}

export function broadcastSources(archive: LscArchiveItem, cards: LscArchiveItem[]): LscArchiveItem[] {
  if (archive.sources && archive.sources.length > 1) {
    return archive.sources;
  }
  const card = cards.find(
    (item) => item.id === archive.id || item.sources?.some((source) => source.id === archive.id)
  );
  if (card?.sources && card.sources.length > 0) {
    return card.sources;
  }
  return [archive];
}

export function moreFromSameWeek(archive: LscArchiveItem, cards: LscArchiveItem[]): LscArchiveItem[] {
  const season = archive.match_summary?.season_label?.trim();
  const stage = archive.match_summary?.stage_label?.trim();
  if (!season || !stage) return [];
  const sources = broadcastSources(archive, cards);
  const ownIds = new Set(sources.map((source) => source.id));
  return cards.filter((card) => {
    if (ownIds.has(card.id)) return false;
    if (card.sources?.some((source) => ownIds.has(source.id))) return false;
    return (
      card.match_summary?.season_label?.trim() === season &&
      card.match_summary?.stage_label?.trim() === stage
    );
  });
}
